/**
 * Resident todos data hook — real API (api/resident-todos via RTK Query).
 * Components consume THIS seam only; the slice stays an implementation
 * detail. The mock era's contract (todos/toggleTodo/isLoading) is preserved
 * and extended with CRUD + fetch-state (spec §6).
 */
import { useCallback } from 'react';
import type { ResidentTodo } from '@/types/residents/todos';
import {
  useAddResidentTodoMutation,
  useDeleteResidentTodoMutation,
  useGetResidentTodosQuery,
  useUpdateResidentTodoMutation,
} from '@/store/api/residentTodosApi';
import type {
  CreateResidentTodoInput,
  UpdateResidentTodoInput,
} from '@/store/api/residentTodosApi';

export interface UseResidentTodosResult {
  todos: ResidentTodo[];
  toggleTodo: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  addTodo: (input: CreateResidentTodoInput) => Promise<ResidentTodo>;
  updateTodo: (input: UpdateResidentTodoInput) => Promise<ResidentTodo>;
  deleteTodo: (id: string) => Promise<void>;
}

const EMPTY_TODOS: ResidentTodo[] = [];

export function useResidentTodos(): UseResidentTodosResult {
  const { data, isLoading, isError, refetch } = useGetResidentTodosQuery();
  const [addMutation] = useAddResidentTodoMutation();
  const [updateMutation] = useUpdateResidentTodoMutation();
  const [deleteMutation] = useDeleteResidentTodoMutation();

  const todos = data ?? EMPTY_TODOS;

  // Fire-and-forget on purpose: the slice's optimistic patch flips the row
  // instantly and rolls back on error (spec §7 states).
  const toggleTodo = useCallback(
    (id: string) => {
      const current = todos.find(t => t.id === id);
      if (!current) return;
      void updateMutation({ id, completed: !current.completed });
    },
    [todos, updateMutation],
  );

  const addTodo = useCallback(
    (input: CreateResidentTodoInput) => addMutation(input).unwrap(),
    [addMutation],
  );
  const updateTodo = useCallback(
    (input: UpdateResidentTodoInput) => updateMutation(input).unwrap(),
    [updateMutation],
  );
  const deleteTodo = useCallback(
    async (id: string) => {
      await deleteMutation({ id }).unwrap();
    },
    [deleteMutation],
  );

  return { todos, toggleTodo, isLoading, isError, refetch, addTodo, updateTodo, deleteTodo };
}
